/**
 * Hook ดึงผลรางวัลหวย — ตรง user-classic (Public API, dateFrom/dateTo = วันที่เลือก, return ทุก section)
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { lotteryService, type LotteryRound } from '../services/lotteryService'
import {
  extractRoundFromRealtimeDetail,
  isRoundEligibleForResultsDisplay,
  mergeRoundIntoList,
} from '@huay/shared/lotteryRoundRealtime'
import {
  isThaiGovernmentLotteryOnly,
  isGovernmentLottery,
  isEgovRound,
  isGsbRound,
  isYeekeeRound,
  isYeekeeVipRound,
  getSectionKind,
  roundMatchesHuayType,
} from '../utils/lotteryResultSectionUtils'
import {
  isRoundVisibleOnDate,
  isRoundNumberDateExactly,
  getRoundDrawDateString,
  sortRoundsForResultDisplay,
  hasAnyResult,
} from '../utils/resultDisplayUtils'
import { compareRoundsForLottoDisplay } from '../services/lotteryService'

const MAX_DISPLAY_ROUNDS_PER_SECTION = 500

/** ดึงรอบหวยทั้งวัน — HuayToday มี ~280+ รอบ/วัน (ยี่กีเยอะ); API รองรับสูงสุด 2000 */
const PUBLIC_LOTTERY_ROUNDS_FETCH_LIMIT = 1000

/** ช่วงเวลาปัดผลรางวัลอัตโนมัติ (ms) — 0 = ไม่ปัด */
const DEFAULT_POLLING_INTERVAL_MS = 15_000

/** ย้อนหลังกี่เดือนของงวด 1 / 16 เพื่อหาผลหวยรัฐบาลไทยล่าสุด */
const GOV_CARRY_FORWARD_MONTHS = 4

function addDaysYYYYMMDD(dateStr: string, deltaDays: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  if (!y || !m || !d) return dateStr
  const utc = Date.UTC(y, m - 1, d + deltaDays)
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(utc))
}

function isThaiGovRoundCandidate(round: LotteryRound): boolean {
  if (isThaiGovernmentLotteryOnly(round)) return true
  return isGovernmentLottery(round) && !isEgovRound(round) && !isGsbRound(round)
}

/** วันที่งวดรัฐบาลไทยที่พบบ่อย (1 และ 16) เรียงใหม่ → เก่า ภายในช่วง selectedDate */
function recentGovDrawCandidateDates(selectedDate: string): string[] {
  const [y, m] = selectedDate.split('-').map(Number)
  if (!y || !m) return []
  const dates: string[] = []
  for (let i = 0; i < GOV_CARRY_FORWARD_MONTHS; i++) {
    let month = m - i
    let year = y
    while (month <= 0) {
      month += 12
      year -= 1
    }
    const mm = String(month).padStart(2, '0')
    dates.push(`${year}-${mm}-16`)
    dates.push(`${year}-${mm}-01`)
  }
  return [...new Set(dates)]
    .filter((d) => d <= selectedDate)
    .sort((a, b) => b.localeCompare(a))
}

/** เลือกรอบล่าสุดที่มีผลแล้ว (หรือ announced) สำหรับแสดงค้างจนกว่าจะมีรอบใหม่ */
function pickLatestAnnounced(
  pool: LotteryRound[],
  filterFn: (r: LotteryRound) => boolean,
): LotteryRound | null {
  const candidates = pool.filter(filterFn)
  if (!candidates.length) return null
  const withResult = candidates.filter(
    (r) =>
      hasAnyResult(r) ||
      ['announced', 'completed', 'settled'].includes(String(r.status || '').toLowerCase()),
  )
  const source = withResult.length > 0 ? withResult : candidates
  const sorted = [...source].sort((a, b) => {
    const drawA = getRoundDrawDateString(a) || ''
    const drawB = getRoundDrawDateString(b) || ''
    if (drawA && drawB && drawA !== drawB) return drawB.localeCompare(drawA)
    const endA = new Date(a.betEndTime).getTime()
    const endB = new Date(b.betEndTime).getTime()
    if (Number.isFinite(endA) && Number.isFinite(endB) && endA !== endB) return endB - endA
    return compareRoundsForLottoDisplay(b, a)
  })
  return sorted[0] ?? null
}

function pickLatestAnnouncedThaiGov(pool: LotteryRound[]): LotteryRound | null {
  return pickLatestAnnounced(pool, isThaiGovRoundCandidate)
}

export interface UseLotteryResultsOptions {
  targetRoundId?: string | null
  refreshTrigger?: number
  /** อัปเดตผลรางวัลอัตโนมัติทุก N ms (0 = ปิด) */
  pollingIntervalMs?: number
}

export interface UseLotteryResultsReturn {
  poolError: string | null
  loading: boolean
  latestGovernmentRound: LotteryRound | null
  latestYeekeeRound: LotteryRound | null
  /** รอบยี่กีล่าสุดหลายรอบ (ปิดรับแล้ว) — สำหรับเติมการ์ด featured */
  recentYeekeeRounds: LotteryRound[]
  latestEgovRound: LotteryRound | null
  latestGsbRound: LotteryRound | null
  latestSetRounds: LotteryRound[]
  latestThaiStockRounds: LotteryRound[]
  latestForeignRounds: LotteryRound[]
  latestForeignStockRounds: LotteryRound[]
  latestVipRounds: LotteryRound[]
  latestVisaRounds: LotteryRound[]
  allYeekeeVipRounds: LotteryRound[]
}

/** เลือกรอบล่าสุด — ถ้ามีรอบที่งวดตรงกับ selectedDate จะเลือกนั้นก่อน (ตรง user-classic + sort by draw date) */
function pickLatest(
  pool: LotteryRound[],
  selectedDate: string,
  filterFn: (r: LotteryRound) => boolean,
  max = 1
): LotteryRound[] {
  if (!pool.length) return []
  const filtered = pool.filter(filterFn)
  if (!filtered.length) return []
  const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
  const source = visible.length > 0 ? visible : filtered
  const sorted = [...source].sort((a, b) => {
    const aDraw = getRoundDrawDateString(a)
    const bDraw = getRoundDrawDateString(b)
    const aMatch = aDraw === selectedDate ? 1 : 0
    const bMatch = bDraw === selectedDate ? 1 : 0
    if (bMatch !== aMatch) return bMatch - aMatch
    return compareRoundsForLottoDisplay(a, b)
  })
  return sorted.slice(0, max)
}

function sortByDrawDateThenBetEnd<T extends LotteryRound>(list: T[], selectedDate: string): T[] {
  return sortRoundsForResultDisplay(list, selectedDate)
}

function pickByHuayType(
  pool: LotteryRound[],
  selectedDate: string,
  huayType: { _id?: string; huayTypeId?: string; name?: string; code?: string } | null,
  max = MAX_DISPLAY_ROUNDS_PER_SECTION
): LotteryRound[] {
  if (!pool.length || !huayType) return []
  const filtered = pool.filter((r) => roundMatchesHuayType(r, huayType))
  if (!filtered.length) return []
  const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
  const source = visible.length > 0 ? visible : filtered
  return sortByDrawDateThenBetEnd(source, selectedDate).slice(0, max)
}

export function useLotteryResults(
  selectedDate: string,
  options: UseLotteryResultsOptions = {},
  tenantId?: string
): UseLotteryResultsReturn {
  const { targetRoundId = null, refreshTrigger = 0, pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS } = options
  const [pool, setPool] = useState<LotteryRound[]>([])
  const [pollTick, setPollTick] = useState(0)
  const [huayTypes, setHuayTypes] = useState<
    Array<{ _id: string; huayTypeId?: string; name: string; code?: string; configuration?: { isActive?: boolean } }>
  >([])
  const [poolError, setPoolError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  /** true หลังโหลดสำเร็จครั้งแรก — poll รอบถัดไปไม่สลับ loading (กันหน้าเด้ง) */
  const hasLoadedOnceRef = useRef(false)
  /** ผลรอบล่าสุดของครอบครัวรัฐบาล (รัฐบาล / ธ.ก.ส. / ออมสิน) — เมื่อวันเลือกยังไม่มีรอบ */
  const [govCarryForward, setGovCarryForward] = useState<LotteryRound | null>(null)
  const [egovCarryForward, setEgovCarryForward] = useState<LotteryRound | null>(null)
  const [gsbCarryForward, setGsbCarryForward] = useState<LotteryRound | null>(null)

  // เปลี่ยนวัน/tenant/เป้า → ให้โชว์ loading ใหม่ (ไม่รวม pollTick)
  useEffect(() => {
    hasLoadedOnceRef.current = false
  }, [selectedDate, tenantId, targetRoundId, refreshTrigger])

  useEffect(() => {
    // guard: wait for tenantId to be resolved (empty string or undefined = not yet ready)
    if (!selectedDate || tenantId === '' || tenantId === undefined) {
      setLoading(false)
      return
    }
    // sync tenantId into singleton before fetching (if method exists)
    if (tenantId && typeof (lotteryService as { setTenantId?: unknown }).setTenantId === 'function') {
      (lotteryService as { setTenantId: (id: string) => void }).setTenantId(tenantId)
    }
    let cancelled = false
    setPoolError(null)
    if (!hasLoadedOnceRef.current) setLoading(true)
    // ตรง user-classic: ดึงแค่วันต่อวัน (dateFrom/dateTo = วันที่เลือก) ไม่ขยาย ±14 วัน
    const load = targetRoundId
      ? Promise.all([
          lotteryService.getLotteryRounds({ roundId: targetRoundId, limit: 1 }),
          lotteryService.getTenantHuayTypes({ limit: 100 }),
        ]).then(([list, types]) => ({ list: list || [], types: types || [] }))
      : Promise.all([
          lotteryService.getLotteryRounds({
            dateFrom: selectedDate,
            dateTo: selectedDate,
            limit: PUBLIC_LOTTERY_ROUNDS_FETCH_LIMIT,
            timezone: 'Asia/Bangkok',
          }),
          lotteryService.getTenantHuayTypes({ limit: 100 }),
        ]).then(([list, types]) => ({ list: list || [], types: types || [] }))

    load
      .then(({ list, types }) => {
        if (cancelled) return
        if (typeof window !== 'undefined' && import.meta.env.DEV && list.length > 0) {
          const bySection = list.reduce(
            (acc, r) => {
              const k = getSectionKind(r)
              acc[k] = (acc[k] || 0) + 1
              return acc
            },
            {} as Record<string, number>
          )
          console.log('[useLotteryResults] Raw API:', { total: list.length, date: selectedDate, bySection })
        }
        setPool(list)
        setHuayTypes(types)
        hasLoadedOnceRef.current = true
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[useLotteryResults] API error:', err)
        setPoolError(err?.message || 'โหลดข้อมูลไม่ได้')
        setPool([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [targetRoundId, selectedDate, refreshTrigger, pollTick, tenantId])

  const handleRoundRealtimeUpdate = useCallback((event?: Event) => {
    const detail = (event as CustomEvent | undefined)?.detail
    const content =
      detail && typeof detail === 'object' && 'content' in detail
        ? (detail as { content?: unknown }).content
        : detail
    const roundDetail =
      extractRoundFromRealtimeDetail(detail) ??
      (content && typeof content === 'object'
        ? extractRoundFromRealtimeDetail({ round: (content as { round?: unknown }).round ?? content })
        : null)
    if (!roundDetail?._id) return
    setPool((prev) => mergeRoundIntoList(prev, roundDetail as LotteryRound))
  }, [])

  useEffect(() => {
    const onRoundUpdated = (event: Event) => handleRoundRealtimeUpdate(event)
    window.addEventListener('lottery_round_updated', onRoundUpdated)
    window.addEventListener('lottery_round_update', onRoundUpdated)
    window.addEventListener('content_update', onRoundUpdated)
    return () => {
      window.removeEventListener('lottery_round_updated', onRoundUpdated)
      window.removeEventListener('lottery_round_update', onRoundUpdated)
      window.removeEventListener('content_update', onRoundUpdated)
    }
  }, [handleRoundRealtimeUpdate])

  const resultsPool = useMemo(
    () => pool.filter((round) => isRoundEligibleForResultsDisplay(round)),
    [pool],
  )

  const todayThaiGovRound = useMemo(
    () =>
      resultsPool.find(
        (r) => isThaiGovRoundCandidate(r) && isRoundVisibleOnDate(r, selectedDate),
      ) ?? null,
    [resultsPool, selectedDate],
  )
  const hasThaiGovOnSelectedDate = Boolean(todayThaiGovRound)

  const hasEgovOnSelectedDate = useMemo(
    () => resultsPool.some((r) => isEgovRound(r) && isRoundVisibleOnDate(r, selectedDate)),
    [resultsPool, selectedDate],
  )
  const hasGsbOnSelectedDate = useMemo(
    () => resultsPool.some((r) => isGsbRound(r) && isRoundVisibleOnDate(r, selectedDate)),
    [resultsPool, selectedDate],
  )
  const todayGovDraw = todayThaiGovRound ? getRoundDrawDateString(todayThaiGovRound) : null
  const todayGovId = todayThaiGovRound?._id ?? null

  // ค้างผลครอบครัวรัฐบาล (รัฐบาล / ธ.ก.ส. / ออมสิน) ตามงวด 1/16 เดียวกัน
  useEffect(() => {
    const clearAll = () => {
      setGovCarryForward(null)
      setEgovCarryForward(null)
      setGsbCarryForward(null)
    }

    if (!selectedDate || tenantId === '' || tenantId === undefined || targetRoundId) {
      clearAll()
      return
    }

    const needGov = !hasThaiGovOnSelectedDate
    const needEgov = !hasEgovOnSelectedDate
    const needGsb = !hasGsbOnSelectedDate
    if (!needGov && !needEgov && !needGsb) {
      clearAll()
      return
    }

    // ถ้ามีรอบรัฐบาลวันนี้แล้ว — ให้งวดธนาคารตามวันที่งวดของรอบนั้นก่อน
    const candidateDates = [
      ...(todayGovDraw ? [todayGovDraw] : []),
      ...recentGovDrawCandidateDates(selectedDate),
    ].filter((d, i, arr) => Boolean(d) && arr.indexOf(d) === i)

    let cancelled = false

    ;(async () => {
      // ดึงทีละวัน + limit สูง — ช่วงหลายวันด้วย limit ต่ำจะเต็มด้วยยี่กี/VIP
      for (const drawDate of candidateDates) {
        if (cancelled) return
        const daysToProbe = [
          drawDate,
          addDaysYYYYMMDD(drawDate, -1),
          addDaysYYYYMMDD(drawDate, 1),
          addDaysYYYYMMDD(drawDate, -2),
        ].filter((d, i, arr) => d <= selectedDate && arr.indexOf(d) === i)

        const collected: LotteryRound[] = []
        for (const day of daysToProbe) {
          if (cancelled) return
          try {
            const list = await lotteryService.getLotteryRounds({
              dateFrom: day,
              dateTo: day,
              limit: PUBLIC_LOTTERY_ROUNDS_FETCH_LIMIT,
              timezone: 'Asia/Bangkok',
            })
            if (cancelled) return
            collected.push(...(list || []))
          } catch (err) {
            console.warn('[useLotteryResults] gov-family carry-forward fetch failed:', day, err)
          }
        }

        const eligible = collected.filter((round) => isRoundEligibleForResultsDisplay(round))
        const foundGov = pickLatestAnnouncedThaiGov(eligible)
        const foundEgov = pickLatestAnnounced(eligible, isEgovRound)
        const foundGsb = pickLatestAnnounced(eligible, isGsbRound)

        // หยุดที่งวดล่าสุดที่มีรัฐบาล (หรือมีธนาคารเมื่อไม่ต้องหา gov แล้ว)
        const periodReady = needGov ? Boolean(foundGov) : Boolean(foundGov || foundEgov || foundGsb)
        if (!periodReady) continue

        if (needGov) setGovCarryForward(foundGov)
        else setGovCarryForward(null)
        if (needEgov) setEgovCarryForward(foundEgov)
        else setEgovCarryForward(null)
        if (needGsb) setGsbCarryForward(foundGsb)
        else setGsbCarryForward(null)
        return
      }
      if (!cancelled) {
        if (needGov) setGovCarryForward(null)
        if (needEgov) setEgovCarryForward(null)
        if (needGsb) setGsbCarryForward(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    selectedDate,
    tenantId,
    targetRoundId,
    hasThaiGovOnSelectedDate,
    hasEgovOnSelectedDate,
    hasGsbOnSelectedDate,
    todayGovDraw,
    todayGovId,
    refreshTrigger,
  ])

  // อัปเดตผลรางวัลอัตโนมัติเรื่อยๆ แม้ไม่มี activity (polling)
  useEffect(() => {
    if (!pollingIntervalMs || pollingIntervalMs <= 0) return
    const id = setInterval(() => setPollTick((t) => t + 1), pollingIntervalMs)
    return () => clearInterval(id)
  }, [pollingIntervalMs])

  const activeHuayTypes = useMemo(
    () => huayTypes.filter((ht) => ht.configuration?.isActive !== false),
    [huayTypes]
  )

  const vipHuayType = useMemo(
    () => activeHuayTypes.find((ht) => /หวย\s*vip|vip/i.test(ht.name || '')),
    [activeHuayTypes]
  )

  const visaHuayType = useMemo(
    () => activeHuayTypes.find((ht) => /หวย\s*visa|visa/i.test(ht.name || '')),
    [activeHuayTypes]
  )

  /** ประเภทหวยยี่กี — รองรับทั้ง "ยี่กี" และ "ยีกี่" (สะกดต่างกันในระบบ) */
  const yeekeeHuayType = useMemo(
    () =>
      activeHuayTypes.find(
        (ht) =>
          /หวย\s*ยี่กี|หวย\s*ยีกี่|จับ\s*ยี่กี|จับ\s*ยีกี่|yeekee|ยี่กี|ยีกี่/i.test(ht.name || '') ||
          /yeekee|lyk/i.test(ht.code || '')
      ),
    [activeHuayTypes]
  )

  const thaiStockHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter((ht) => /หุ้นไทย|หวยหุ้นไทย|thai\s*stock/i.test(ht.name || '')),
    [activeHuayTypes]
  )

  const foreignHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter(
        (ht) =>
          (/ต่างประเทศ|foreign|international/i.test(ht.name || '') || /foreign|international/i.test(ht.code || '')) &&
          !/หุ้น/i.test(ht.name || '')
      ),
    [activeHuayTypes]
  )

  const foreignStockHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter(
        (ht) =>
          /หุ้นต่างประเทศ|foreign\s*stock|หวยหุ้น|stock/i.test(ht.name || '') ||
          (/หุ้น/i.test(ht.name || '') && !/หุ้นไทย|ไทย/i.test(ht.name || '')) ||
          /stock/i.test(ht.code || '')
      ),
    [activeHuayTypes]
  )

  const activeSetHuayTypes = useMemo(
    () =>
      activeHuayTypes.filter(
        (ht) => /หวย\s*ชุด|เลขชุด|ชุด/i.test(ht.name || '') || /set/i.test(ht.code || '')
      ),
    [activeHuayTypes]
  )

  const latestGovernmentRound = useMemo(() => {
    let gov = pickLatest(resultsPool, selectedDate, isThaiGovernmentLotteryOnly, 1)
    // Fallback must STILL exclude ธ.ก.ส./ออมสิน — they share a HuayType named
    // "หวยรัฐบาลไทย", so the broad isGovernmentLottery wrongly pulled a BAAC/GSB round
    // (wrong name/numbers/logo) into the gov card when the real gov round was absent.
    if (gov.length === 0)
      gov = pickLatest(resultsPool, selectedDate, (r) => isGovernmentLottery(r) && !isEgovRound(r) && !isGsbRound(r), 1)
    const fromPool = gov[0] ?? null
    if (fromPool && isRoundVisibleOnDate(fromPool, selectedDate)) return fromPool
    // ไม่มีรอบในวันเลือก → แสดงผลรอบล่าสุดที่ออกแล้ว (carry-forward) พร้อมงวดของรอบนั้น
    return govCarryForward ?? fromPool
  }, [resultsPool, selectedDate, govCarryForward])

  const latestEgovRound = useMemo(() => {
    const fromPool = pickLatest(resultsPool, selectedDate, isEgovRound, 1)[0] ?? null
    if (fromPool && isRoundVisibleOnDate(fromPool, selectedDate)) return fromPool
    return egovCarryForward ?? fromPool
  }, [resultsPool, selectedDate, egovCarryForward])

  const latestGsbRound = useMemo(() => {
    const fromPool = pickLatest(resultsPool, selectedDate, isGsbRound, 1)[0] ?? null
    if (fromPool && isRoundVisibleOnDate(fromPool, selectedDate)) return fromPool
    return gsbCarryForward ?? fromPool
  }, [resultsPool, selectedDate, gsbCarryForward])

  const latestSetRounds = useMemo(() => {
    if (!resultsPool.length || activeSetHuayTypes.length === 0) return []
    const filtered = resultsPool.filter(
      (r) =>
        getSectionKind(r) === 'set' && activeSetHuayTypes.some((ht) => roundMatchesHuayType(r, ht))
    )
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return sortByDrawDateThenBetEnd(source, selectedDate).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [resultsPool, activeSetHuayTypes, selectedDate])

  const latestThaiStockRounds = useMemo(() => {
    if (!resultsPool.length || thaiStockHuayTypes.length === 0) return []
    const filtered = resultsPool.filter(
      (r) =>
        getSectionKind(r) === 'thai_stock' &&
        thaiStockHuayTypes.some((ht) => roundMatchesHuayType(r, ht))
    )
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return sortByDrawDateThenBetEnd(source, selectedDate).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [resultsPool, thaiStockHuayTypes, selectedDate])

  const latestForeignRounds = useMemo(() => {
    if (!resultsPool.length || foreignHuayTypes.length === 0) return []
    const filtered = resultsPool.filter(
      (r) =>
        getSectionKind(r) === 'foreign' && foreignHuayTypes.some((ht) => roundMatchesHuayType(r, ht))
    )
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return sortByDrawDateThenBetEnd(source, selectedDate).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [resultsPool, foreignHuayTypes, selectedDate])

  const latestForeignStockRounds = useMemo(() => {
    if (!resultsPool.length || foreignStockHuayTypes.length === 0) return []
    const filtered = resultsPool.filter((r) => foreignStockHuayTypes.some((ht) => roundMatchesHuayType(r, ht)))
    const visible = filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : filtered
    return sortByDrawDateThenBetEnd(source, selectedDate).slice(0, MAX_DISPLAY_ROUNDS_PER_SECTION)
  }, [resultsPool, foreignStockHuayTypes, selectedDate])

  const latestVipRounds = useMemo(
    () => pickByHuayType(resultsPool, selectedDate, vipHuayType ?? null),
    [resultsPool, selectedDate, vipHuayType]
  )
  const latestVisaRounds = useMemo(
    () => pickByHuayType(resultsPool, selectedDate, visaHuayType ?? null),
    [resultsPool, selectedDate, visaHuayType]
  )

  const allYeekeeVipRounds = useMemo(() => {
    if (!resultsPool.length || !yeekeeHuayType) return []
    const filtered = resultsPool.filter((r) => roundMatchesHuayType(r, yeekeeHuayType))
    const sameDayOnly = filtered.filter((r) => isRoundNumberDateExactly(r, selectedDate))
    const visible =
      sameDayOnly.length > 0 ? sameDayOnly : filtered.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    return [...visible].sort(
      (a, b) => new Date(a.betStartTime).getTime() - new Date(b.betStartTime).getTime()
    )
  }, [resultsPool, yeekeeHuayType, selectedDate])

  // Debug หวยยี่กี: ดูว่าไม่เจอเพราะ API ไม่ส่งมา หรือ huayType ไม่ match หรือวันที่กรองออก
  useEffect(() => {
    if (!import.meta.env.DEV || !resultsPool.length) return
    const yeekeeRoundsInPool = resultsPool.filter(
      (r) =>
        /ยี่กี|yeekee|ยี/i.test(String(r.huayTypeName || '')) ||
        /ยี่กี|yeekee|ยี/i.test(String(r.huayName || ''))
    )
    const yeekeeTypeId = yeekeeHuayType ? String(yeekeeHuayType.huayTypeId ?? yeekeeHuayType._id ?? '') : ''
    const matchByTypeId = yeekeeTypeId
      ? resultsPool.filter((r) => String(r.huayTypeId ?? '') === yeekeeTypeId)
      : []
    const matchByName =
      yeekeeHuayType?.name
        ? resultsPool.filter(
            (r) =>
              String(r.huayTypeName || '').trim() === String(yeekeeHuayType!.name || '').trim()
          )
        : []
    console.log('[useLotteryResults] หวยยี่กี debug:', {
      selectedDate,
      poolTotal: pool.length,
      activeHuayTypeNames: activeHuayTypes.map((ht) => ({ name: ht.name, code: ht.code, _id: ht._id })),
      yeekeeHuayType: yeekeeHuayType
        ? { name: yeekeeHuayType.name, code: yeekeeHuayType.code, _id: yeekeeHuayType._id, huayTypeId: yeekeeHuayType.huayTypeId }
        : null,
      yeekeeRoundsInPoolByName: yeekeeRoundsInPool.length,
      sampleYeekeeRound: yeekeeRoundsInPool[0]
        ? {
            huayTypeId: yeekeeRoundsInPool[0].huayTypeId,
            huayTypeName: yeekeeRoundsInPool[0].huayTypeName,
            huayName: yeekeeRoundsInPool[0].huayName,
            roundNumber: yeekeeRoundsInPool[0].roundNumber,
          }
        : null,
      matchByTypeIdCount: matchByTypeId.length,
      matchByNameCount: matchByName.length,
      allYeekeeVipRoundsCount: allYeekeeVipRounds.length,
    })
  }, [pool, activeHuayTypes, yeekeeHuayType, selectedDate, allYeekeeVipRounds.length])

  /** จับยี่กี (ไม่ใช่ VIP) — แสดงรอบที่ปิดรับแล้ว ตรง user-classic */
  const recentYeekeeRounds = useMemo(() => {
    if (!resultsPool.length) return []
    const isVipRound = vipHuayType
      ? (r: LotteryRound) => roundMatchesHuayType(r, vipHuayType)
      : isYeekeeVipRound
    const yeekeeOnly = resultsPool.filter(
      (r) => isYeekeeRound(r) && !isVipRound(r) && r.status !== 'open'
    )
    const visible = yeekeeOnly.filter((r) => isRoundVisibleOnDate(r, selectedDate))
    const source = visible.length > 0 ? visible : yeekeeOnly
    return [...source]
      .sort((a, b) => new Date(b.betEndTime).getTime() - new Date(a.betEndTime).getTime())
      .slice(0, 8)
  }, [resultsPool, vipHuayType, selectedDate])

  const latestYeekeeRound = useMemo(
    () => recentYeekeeRounds[0] ?? null,
    [recentYeekeeRounds]
  )

  return {
    poolError,
    loading,
    latestGovernmentRound,
    latestYeekeeRound,
    recentYeekeeRounds,
    latestEgovRound,
    latestGsbRound,
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
  }
}
